/* eslint-disable sonarjs/no-duplicate-string */
// External
import { Injectable } from '@angular/core';

// Internal
import { ConfigService } from '../config/config.service';
import {
    EventTypes,
    EventClass,
    EventsTypeInterface,
    EventsClassInterface,
    ThreatEventCategory,
    ThreatEventCategoryInterface,
    ThreatEventsInterface,
    EventThreatType,
    EventThreatSubtype,
    ExplainableThreatsCodes,
    IExplainableThreatsDetails,
    VideoStatuses,
    VideoStatusesInterface
} from '../models/ato/AtoEvents.model';
import { ImageDescription, ImageDescriptionMap } from './values.service';
import { AtoErrorCodes, AtoProviders, ErrorCodes2TextsMapping } from '../models/ato/AtoService.model';
import { IconInterface } from '../models/ato/AtoGeneral.model';

type ThreatSubtypeIcons = {
    [key in EventThreatSubtype]?: IconInterface
};

type ThreatTypeIcons = {
    [key in EventThreatType]?: ThreatSubtypeIcons
};

type AtoProviderConfig = {
    id: string;
    icon: ImageDescription;
    company: string;
    title: string;
    cta: string;
    desc: string;
};

export enum FeedbackModalScenarios {
    FULL = 'full',
    SHORT = 'short'
};

@Injectable({
    providedIn: 'root'
})

export class ATOValuesService {

    constructor(
        private readonly configService: ConfigService
    ) { }

    public atoLastImagesNo = 3;

    public readonly images: ImageDescriptionMap = {
        setupWelcomeStep: {
            link: this.configService.config.pathImages.concat('ato/ill_set_up_ato.svg'),
            alt: ''
        },
        setupConnectYoutubeStepNotConnected: {
            link: this.configService.config.pathImages.concat('ato/ill_connect_youtube.svg'),
            alt: ''
        },
        setupAccountConnected: {
            link: this.configService.config.pathImages.concat('ato/ill_connected.svg'),
            alt: ''
        },
        setupProtectDeviceStepToDownload: {
            link: this.configService.config.pathImages.concat('ato/ill_download.svg'),
            alt: ''
        },
        setupProtectDeviceStepDownloaded: {
            link: this.configService.config.pathImages.concat('ato/ill_download_complete.svg'),
            alt: ''
        },
        setupFinishStep: {
            link: this.configService.config.pathImages.concat('ato/ill_remeber_to_install.svg'),
            alt: ''
        },
        illEmailProtection: {
            link: this.configService.config.pathImages.concat('ato/ill_email_protection.svg'),
            alt: ''
        },
        revokeAccess: {
            link: this.configService.config.pathImages.concat('ato/ill_revoke_access.svg'),
            alt: ''
        },
        noVideos: {
            link: this.configService.config.pathImages.concat('ato/ill_yt_no_videos.svg'),
            alt: ''
        },
        noPosts: {
            link: this.configService.config.pathImages.concat('ato/ill_meta_no_posts.svg'),
            alt: ''
        },
        isSafe: {
            link: this.configService.config.pathImages.concat('ato/ill_is_safe.svg'),
            alt: ''
        },
        subsExpired: {
            link: this.configService.config.pathImages.concat('ato/ill_resubscribe.svg'),
            alt: ''
        },
        explainableThreat: {
            link: this.configService.config.pathImages.concat('ato/explainable_threat.svg'),
            alt: ''
        },
        threatEventWithRcaStartNode: {
            link: this.configService.config.pathImages.concat('ato/root.svg'),
            alt: ''
        },
        threatEventWithRcaNode: {
            link: this.configService.config.pathImages.concat('ato/process.svg'),
            alt: ''
        },
        threatEventWithRcaNode2: {
            link: this.configService.config.pathImages.concat('ato/process2.svg'),
            alt: ''
        },
        threatEventSuccessNode: {
            link: this.configService.config.pathImages.concat('ato/threat_blocked.png'),
            alt: ''
        },
        threatEventErrorNode: {
            link: this.configService.config.pathImages.concat('ato/alternative_outcome.png'),
            alt: ''
        },
        googleLogo: {
            link: this.configService.config.pathImages.concat('common/google_icon.svg'),
            alt: ""
        },
        ytNotAvailable: {
            link: this.configService.config.pathImages.concat('ato/ill_yt_not_available_dots.svg'),
            alt: ""
        },
        monitoredVideosDefault: {
            link: this.configService.config.pathImages.concat('ato/ill_monitored_videos_default.svg'),
            alt: ""
        },
        facebookLogo:{
            link: this.configService.config.pathImages.concat('social-media-accounts/facebook.svg'),
            alt: "Facebook"
        },
        instagramLogo:{
            link: this.configService.config.pathImages.concat('social-media-accounts/instagram.svg'),
            alt: "Instagram"
        },
        youtubeLogo:{
            link: this.configService.config.pathImages.concat('social-media-accounts/youtube.svg'),
            alt: "YouTube"
        },
        suspiciousMessage: {
            link: this.configService.config.pathImages.concat('ato/suspicious_message.svg'),
            alt: ""
        },
        confirmation: {
            link: this.configService.config.pathImages.concat('ato/ill_confirmation.svg'),
            alt: ""
        },
        thoughts: {
            link: this.configService.config.pathImages.concat('ato/ill_thoughts.svg'),
            alt: ""
        }
    };

    public readonly errorCodes2TextsMapping: ErrorCodes2TextsMapping = {
        [AtoErrorCodes.INVALID_SERVICE_PROVIDER_ACCOUNT]: {
            [AtoProviders.YOUTUBE]: {
                title: 'ato.errors.service.not.found.title',
                desc: 'ato.errors.service.not.found.desc'
            },
            [AtoProviders.INSTAGRAM]: {
                title: 'ato.errors.service.not.found.ig.title',
                desc: 'ato.errors.service.not.found.ig.desc'
            },
            [AtoProviders.FACEBOOK]: {
                title: 'ato.errors.service.not.found.fb.title',
                desc: 'ato.errors.service.not.found.fb.desc'
            },
            default: {
                title: 'ato.errors.service.not.found.default.title',
                desc: 'ato.errors.service.not.found.default.desc'
            }
        },
        [AtoErrorCodes.INVALID_SERVICE_SCOPES]: {
            default: {
                title: 'ato.errors.invalid.service.scopes.title',
                desc: 'ato.errors.invalid.service.scopes.desc'
            }
        },
        [AtoErrorCodes.NO_SUBSCRIPTIONS]: {
            default: {
                title: 'ato.subs.expired.title',
                desc: 'ato.subs.expired.desc'
            }
        },
        [AtoErrorCodes.DEFAULT]: {
            default: {
                title: 'ato.reconnect.not.ok.setup.title',
                desc: 'ato.errors.generic'
            }
        }
    };

    public readonly omnitureInfoPlaceholder = {
        event_type: '{{event_type}}',
        provider: '{{provider}}',
    };

    public readonly omnitureInfo = {
        events: {
            markAsDoneEvent: `markAsDoneEvent_${this.omnitureInfoPlaceholder.event_type}`,
            setFalsePositiveEvent: `setFalsePositiveEvent_${this.omnitureInfoPlaceholder.event_type}`,
            fixErrorsRecomendation: `fixErrorsRecomendation_${this.omnitureInfoPlaceholder.event_type}`,
            fixErrorsCritical: `fixErrorsCritical_${this.omnitureInfoPlaceholder.event_type}`,
            hideEvent: `hideEvent_${this.omnitureInfoPlaceholder.event_type}`,
            installEventCritical: `installEventCritical_${this.omnitureInfoPlaceholder.event_type}`,
            installEventRecomendation: `installEventRecomendation_${this.omnitureInfoPlaceholder.event_type}`,
            openReviewlink: `openReviewlink_${this.omnitureInfoPlaceholder.event_type}`,
            openMediaLink: `openMediaLink_${this.omnitureInfoPlaceholder.event_type}`,
            openFalsePositiveModal: `openFalsePositiveModal_${this.omnitureInfoPlaceholder.event_type}`,
            openStartAtoRecovery: `openStartAtoRecovery_${this.omnitureInfoPlaceholder.event_type}`,
            closeStartAtoRecovery: `closeStartAtoRecovery_${this.omnitureInfoPlaceholder.event_type}`,
            openThreatsEmailRecomandation: `openThreatsEmailRecomandation_${this.omnitureInfoPlaceholder.event_type}`,
            openThreatsEmailCritical: `openThreatsEmailCritical_${this.omnitureInfoPlaceholder.event_type}`,
            openTeam: `openTeam_${this.omnitureInfoPlaceholder.event_type}`,
            undoAction: `undoAction_${this.omnitureInfoPlaceholder.event_type}`,
            usefulEvent: `usefulEvent_${this.omnitureInfoPlaceholder.event_type}`,
            openWhatHappenedSection: `openWhatHappenedSection_${this.omnitureInfoPlaceholder.event_type}`,
            openAccessAccountSections: `openAccessAccountSections_${this.omnitureInfoPlaceholder.event_type}`,
            openResetPassSection: `openResetPassSection_${this.omnitureInfoPlaceholder.event_type}`,
            openReviewSettingsSection: `openReviewSettingsSection_${this.omnitureInfoPlaceholder.event_type}`,
            openCheckInfoSection: `openCheckInfoSection_${this.omnitureInfoPlaceholder.event_type}`,
            openThreatDetails: `openThreatDetails_${this.omnitureInfoPlaceholder.event_type}`,
            closeThreatDetails: `closeThreatDetails_${this.omnitureInfoPlaceholder.event_type}`,
            startSetup: 'startSetup',
            addNewTeamMember: 'addNewTeamMember',
            removeTeamMember: 'removeTeamMember',
            renewSubscription: 'renewSubscription',
            connectAccount: `connectAccount_${this.omnitureInfoPlaceholder.provider}`,
            linkAccount: 'linkAccount',
            selectAccount: `selectAccount_${this.omnitureInfoPlaceholder.provider}`,
            downloadApp: 'downloadApp',
            enableSiblingCard: 'enableSiblingCard',
            revokeAccess: 'revokeAccess',
            keepAccess: 'keepAccess',
            reachedLastStepInRcaFlow: 'reachedLastStepInRcaFlow',
            openTechnologiesModal: 'openTechnologiesModal',
            openLiveReportModal: 'openLiveReportModal',
            openReconnectModal: 'openReconnectModal',
            closeIdentityModal: 'closeIdentityModal',
            reconnectService: 'reconnectService',
            reloadEventsList: 'reloadEventsList',
            recreatingGroup: 'recreatingGroup',
            loadMoreEvents: 'loadMoreEvents',
            showOldEvents: 'showOldEvents'
        },
        sections: {
            setupStart: 'setupStart',
            connectService: 'connectService',
            downloadProtection: 'downloadProtection',
            setupEnd: 'setupEnd'
        }
    };

    public readonly eventClassInterface: EventsClassInterface = {
        [EventClass.ACCOUNT_HEALTH]: {
            type: 'action',
            icon: 'quick-scan',
            label: 'ato.events.class.account.health'
        },
        [EventClass.GUIDED_RECOVERY]: {
            type: 'feature',
            icon: 'what-we-monitor',
            label: 'ato.events.class.guided.recovery'
        },
        [EventClass.DEVICE_PROTECTION]: {
            type: 'feature',
            icon: 'local-protection',
            label: 'ato.events.class.device.protection'
        },
        [EventClass.TEAM_SHIELD]: {
            type: 'feature',
            icon: 'product-Parental',
            label: 'ato.events.class.team.shield'
        },
        [EventClass.PREVENTIVE_ACTIONS]: {
            type: 'user-data',
            icon: 'personal-interests',
            label: 'ato.events.class.preventive.actions'
        },
        [EventClass.SCAM_GUARD]: {
            type: 'feature',
            icon: 'id-hook',
            label: 'ato.technologies.prevention.scam.title'
        }
    };

    public readonly eventTypeInterface: EventsTypeInterface = {
        [EventTypes.SERVICE_ACCOUNT_CONNECTED]: {
            title: 'ato.events.service.account.connected.title',
            desc: 'ato.events.service.account.connected.desc'
        },
        [EventTypes.MONITORED_VIDEOS_NUM]: {
            title: 'ato.events.monitored.videos.num.title',
            desc: 'ato.events.monitored.videos.num.desc',
            one: {
                title: 'ato.events.monitored.videos.num.title.singular'
            }
        },
        [EventTypes.NEW_PLAYLIST]: {
            icon: {
                type: 'user-data',
                icon: 'video-list'
            },
            title: 'ato.events.new.playlist.title',
            desc: 'ato.events.new.playlist.desc'
        },
        [EventTypes.NEW_CHANNEL_DESCRIPTION]: {
            icon: {
                type: 'web',
                icon: 'blogs'
            },
            title: 'ato.events.new.channel.description.title',
            desc: 'ato.events.new.channel.description.desc'
        },
        [EventTypes.PLAYLIST_DELETED]: {
            icon: {
                type: 'user-data',
                icon: 'video-list'
            },
            title: 'ato.events.playlist.deleted.title',
            desc: 'ato.events.playlist.deleted.desc'
        },
        [EventTypes.VIDEO_DELETED]: {
            icon: {
                type: 'web',
                icon: 'videos'
            },
            title: 'ato.events.video.deleted.title',
            desc: 'ato.events.video.deleted.desc'
        },
        [EventTypes.NEW_VIDEO]: {
            title: 'ato.events.new.video.1.title',
            desc: 'ato.events.new.video.1.desc'
        },
        [EventTypes.NEW_BANNER]: {
            title: 'ato.events.new.banner.title',
            desc: 'ato.events.new.banner.desc'
        },
        [EventTypes.NEW_PROFILE_PICTURE]: {
            title: 'ato.events.new.profile.picture.title',
            desc: 'ato.events.new.profile.picture.desc'
        },
        [EventTypes.DISCONNECTED_API]: {
            avatar: true,
            title: 'ato.events.disconnected.api.title',
            desc: 'ato.events.disconnected.api.desc'
        },
        [EventTypes.IG_DISCONNECTED_API]: {
            avatar: true,
            title: 'ato.events.disconnected.api.title',
            desc: 'ato.events.disconnected.api.desc'
        },
        [EventTypes.FB_DISCONNECTED_API]: {
            avatar: true,
            title: 'ato.events.disconnected.api.title',
            desc: 'ato.events.disconnected.api.desc'
        },
        [EventTypes.INSTALL_TS_ON_ANOTHER_DEVICE]: {
            icon: {
                type: 'general',
                icon: 'download-v2'
            },
            title: 'ato.events.install.ts.on.another.device.title',
            desc: 'ato.events.install.ts.on.another.device.desc'
        },
        [EventTypes.REVIEW_AUTHORIZED_MEMBERS]: {
            icon: {
                type: 'user-data',
                icon: 'list-check'
            },
            title: 'ato.events.review.authorized.members.title',
            desc: 'ato.events.review.authorized.members.desc',
            actionButton: {
                text: 'ato.review.team.button',
                link: 'https://studio.youtube.com/'
            }
        },
        [EventTypes.REVIEW_3RD_PARTY_APPS]: {
            icon: {
                type: 'menu',
                icon: 'apps'
            },
            title: 'ato.events.review.3rd.party.apps.title',
            desc: 'ato.events.review.3rd.party.apps.desc',
            actionButton: {
                text: 'ato.review.app.button',
                link: 'https://myaccount.google.com/connections'
            }
        },
        [EventTypes.REGULARLY_CHANGE_PASSWORD]: {
            icon: {
                type: 'user-data',
                icon: 'password'
            },
            title: 'ato.events.regularly.change.password.title',
            desc: 'ato.events.regularly.change.password.desc',
            actionButton: {
                text: 'ato.change.button',
                link: 'https://myaccount.google.com/signinoptions/password'
            }
        },
        [EventTypes.REVIEW_YOUR_DEVICES]: {
            icon: {
                type: 'feature',
                icon: 'shield-speech'
            },
            title: 'ato.events.review.your.devices.title',
            desc: 'ato.events.review.your.devices.desc',
            actionButton: {
                text: 'ato.review.devices.button',
                link: 'https://myaccount.google.com/device-activity'
            }
        },
        [EventTypes.REVIEW_RECOVERY_OPTIONS]: {
            icon: {
                type: 'feature',
                icon: 'shield-speech'
            },
            title: 'ato.events.review.recovery.options.title',
            desc: 'ato.events.review.recovery.options.desc',
            actionButton: {
                text: 'ato.review.button',
                link: 'https://myaccount.google.com/security'
            }
        },
        [EventTypes.ACCOUNT_TAKEOVER]: {
            icon: {
                type: 'feedback-filled',
                icon: 'shield-attention',
                bgColor: 'danger-5',
                color: 'danger'
            },
            title: 'ato.events.account.take.over.title',
            desc: 'ato.events.account.take.over.desc'
        },
        [EventTypes.IG_ACCOUNT_TAKEOVER]: {
            icon: {
                type: 'feedback-filled',
                icon: 'shield-attention',
                bgColor: 'danger-5',
                color: 'danger'
            },
            title: 'ato.events.account.take.over.title',
            desc: 'ato.events.account.take.over.desc'
        },
        [EventTypes.FB_ACCOUNT_TAKEOVER]: {
            icon: {
                type: 'feedback-filled',
                icon: 'shield-attention',
                bgColor: 'danger-5',
                color: 'danger'
            },
            title: 'ato.events.account.take.over.title',
            desc: 'ato.events.account.take.over.desc'
        },
        [EventTypes.ACCOUNT_RESTORED]: {
            icon: {
                type: 'feedback-filled',
                icon: 'shield-check',
                bgColor: 'neutral-5',
                color: 'neutral-80'
            },
            title: 'ato.events.account.take.over.fixed.title',
            desc: 'ato.events.account.take.over.fixed.desc'
        },
        [EventTypes.FB_ACCOUNT_RESTORED]: {
            icon: {
                type: 'feedback-filled',
                icon: 'shield-check',
                bgColor: 'neutral-5',
                color: 'neutral-80'
            },
            title: 'ato.events.account.take.over.fixed.title',
            desc: 'ato.events.account.take.over.fixed.desc'
        },
        [EventTypes.IG_ACCOUNT_RESTORED]: {
            icon: {
                type: 'feedback-filled',
                icon: 'shield-check',
                bgColor: 'neutral-5',
                color: 'neutral-80'
            },
            title: 'ato.events.account.take.over.fixed.title',
            desc: 'ato.events.account.take.over.fixed.desc'
        },
        [EventTypes.FINISH_TS_INSTALLATION]: {
            icon: {
                type: 'general',
                icon: 'download-v2',
                bgColor: 'danger-5',
                color: 'danger'
            },
            title: 'ato.events.finish.ts.instalation.title',
            desc: 'ato.events.finish.ts.instalation.desc',
            actionButton: {
                text: 'ato.install.button'
            }
        },
        [EventTypes.SUCCESSFULLY_INSTALLED_TS_ON_DEVICE]: {
            icon: {
                type: 'feature',
                icon: 'shield-plus'
            },
            title: 'ato.events.successfully.installed.ts.on.device.title',
            desc: 'ato.events.successfully.installed.ts.on.device.desc'
        },
        [EventTypes.DEVICE_RISK_WEB_ATTACK_PREVENTION_OFF]: {
            icon: {
                type: 'action',
                icon: 'secure-me-v2',
                bgColor: 'danger-5',
                color: 'danger'
            },
            title: 'ato.events.device.risk.web.attack.prevention.off.title',
            desc: 'ato.events.device.risk.web.attack.prevention.off.desc',
            actionButton: {
                text: 'ato.turn.on.action.label'
            }
        },
        [EventTypes.DEVICE_RISK_ADT_OFF]: {
            icon: {
                type: 'feedback-filled',
                icon: 'shield-attention',
                bgColor: 'danger-5',
                color: 'danger'
            },
            title: 'ato.events.device.risk.adt.off.title',
            desc: 'ato.events.device.risk.adt.off.desc',
            actionButton: {
                text: 'ato.turn.on.action.label'
            }
        },
        [EventTypes.DEVICE_RISK_SHIELD_OFF]: {
            icon: {
                type: 'feedback',
                icon: 'shield-attention',
                bgColor: 'danger-5',
                color: 'danger'
            },
            title: 'ato.events.device.risk.shield.off.title',
            desc: 'ato.events.device.risk.shield.off.desc',
            actionButton: {
                text: 'ato.turn.on.action.label'
            }
        },
        [EventTypes.TEAM_INVITE]: {
            icon: {
                type: 'action',
                icon: 'add-account',
                bgColor: 'primary-5',
                color: 'primary'
            },
            title: 'ato.events.team.invite.title',
            desc: 'ato.events.team.invite.desc'
        },
        [EventTypes.WEBMAIL_PROTECTION_ACTIVATED]: {
            icon: {
                type: 'feature',
                icon: 'account-privacy'
            },
            title: 'ato.events.webmail.protection.activated.title',
            desc: 'ato.events.webmail.protection.activated.desc'
        },
        [EventTypes.ACTIVATE_WEBMAIL_PROTECTION]: {
            icon: {
                type: 'user-data',
                icon: 'email',
                bgColor: 'danger-5',
                color: 'danger'
            },
            title: 'ato.events.webmail.protection.activate.title',
            desc: 'ato.events.webmail.protection.activate.desc',
            actionButton: {
                text: 'ato.enable.label'
            }
        },
        [EventTypes.WEBMAIL_PROTECTION_DISCONNECTED]: {
            icon: {
                type: 'user-data',
                icon: 'email',
                bgColor: 'danger-5',
                color: 'danger'
            },
            title: 'ato.events.webmail.protection.disconnected.title',
            desc: 'ato.events.webmail.protection.disconnected.desc',
            actionButton: {
                text: 'ato.reconnect.cta'
            }
        },
        [EventTypes.WEBMAIL_PROTECTION_INBOX_SCAN]: {
            title: 'ato.events.webmail.protection.daily.inbox.scan.title',
            desc: 'ato.events.webmail.protection.daily.inbox.scan.desc',
            descNoEmail: 'ato.events.webmail.protection.daily.inbox.scan.desc.no.email',
            descSafe: 'ato.events.webmail.protection.daily.inbox.scan.desc.safe',
            descSafeNoEmail: 'ato.events.webmail.protection.daily.inbox.scan.desc.safe.no.email'
        },
        [EventTypes.DEVICE_DISCONNECTED]: {
            icon: {
                type: 'feedback',
                icon: 'shield-x'
            },
            title: 'ato.events.device.disconnected.title',
            desc: 'ato.events.device.disconnected.desc',
            actionButton: {
                text: 'ato.reconnect.cta'
            }
        },
        [EventTypes.VIDEO_STATUS_CHANGED]: {
            title: 'ato.events.video.status.changed.title',
            desc: 'ato.events.new.video.1.desc'
        },
        // insta
        [EventTypes.IG_SERVICE_ACCOUNT_CONNECTED]: {
            title: 'ato.events.service.account.connected.title',
            desc: 'ato.events.meta.service.account.connected.desc'
        },
        [EventTypes.IG_MONITORED_POSTS_NUM]: {
            title: 'ato.events.meta.monitored.posts.num.title',
            desc: 'ato.events.meta.monitored.posts.num.desc',
            one: {
                title: 'ato.events.meta.monitored.posts.num.title.singular'
            }
        },
        [EventTypes.IG_NEW_POST]: {
            title: 'ato.events.meta.new.post.title',
            desc: 'ato.events.meta.new.post.desc'
        },
        [EventTypes.IG_PROFILE_PHOTO_CHANGED]: {
            title: 'ato.events.ig.profile.photo.changed.title',
            desc: 'ato.events.ig.profile.photo.changed.desc'
        },
        [EventTypes.IG_NAME_CHANGED]: {
            icon: {
                type: 'user-data',
                icon: 'name',
            },
            title: 'ato.events.meta.name.changed.title',
            desc: 'ato.events.meta.changed.desc'
        },
        [EventTypes.IG_USERNAME_CHANGED]: {
            icon: {
                type: 'user-data',
                icon: 'name',
            },
            title: 'ato.events.meta.username.changed.title',
            desc: 'ato.events.meta.changed.desc'
        },
        [EventTypes.IG_NEW_BIOGRAPHY]: {
            icon: {
                type: 'web',
                icon: 'blogs'
            },
            title: 'ato.events.meta.new.bio.title',
            desc: 'ato.events.meta.new.bio.desc'
        },
        [EventTypes.IG_POST_DELETED]: {
            icon: {
                type: 'general',
                icon: 'photo'
            },
            title: 'ato.events.meta.post.deleted.title',
            desc: 'ato.events.meta.post.deleted.desc'
        },
        [EventTypes.IG_POST_CHANGED]: {
            title: 'ato.events.meta.post.changed.title',
            desc: 'ato.events.meta.post.changed.desc'
        },
        // [EventTypes.IG_NEW_STORY]: {
        //     title: 'ato.events.instagram.title',
        //     desc: 'ato.events.instagram.desc'
        // },
        [EventTypes.IG_NEW_REEL]: {
            title: 'ato.events.ig.new.reel.title',
            desc: 'ato.events.ig.new.reel.desc'
        },
        [EventTypes.IG_REEL_DELETED]: {
            icon: {
                type: 'web',
                icon: 'videos'
            },
            title: 'ato.events.ig.reel.deleted.title',
            desc: 'ato.events.meta.post.deleted.desc'
        },
        [EventTypes.IG_REEL_CHANGED]: {
            title: 'ato.events.ig.reel.changed.title',
            desc: 'ato.events.ig.reel.changed.desc'
        },
        [EventTypes.IG_REVIEW_3RD_PARTY_APPS]: {
            icon: {
                type: 'menu',
                icon: 'apps'
            },
            title: 'ato.events.review.3rd.party.apps.title',
            desc: 'ato.events.review.3rd.party.apps.desc',
            actionButton: {
                text: 'ato.review.app.button',
                link: 'https://accountscenter.instagram.com/password_and_security/password/change/'
            }
        },
        [EventTypes.IG_REVIEW_RECOVERY_OPTIONS]: {
            icon: {
                type: 'feature',
                icon: 'shield-speech'
            },
            title: 'ato.events.review.recovery.options.title',
            desc: 'ato.events.review.recovery.options.desc',
            actionButton: {
                text: 'ato.review.button',
                link: 'https://accountscenter.instagram.com/personal_info/contact_points'
            }
        },
        [EventTypes.IG_REGULARLY_CHANGE_PASSWORD]: {
            icon: {
                type: 'user-data',
                icon: 'password'
            },
            title: 'ato.events.regularly.change.password.title',
            desc: 'ato.events.regularly.change.password.desc',
            actionButton: {
                text: 'ato.change.button',
                link: 'https://accountscenter.instagram.com/password_and_security/password/change/'
            }
        },
        [EventTypes.IG_REVIEW_YOUR_DEVICES]: {
            icon: {
                type: 'feature',
                icon: 'shield-speech'
            },
            title: 'ato.events.review.your.devices.title',
            desc: 'ato.events.review.your.devices.desc',
            actionButton: {
                text: 'ato.review.devices.button',
                link: 'https://accountscenter.instagram.com/password_and_security/login_activity/'
            }
        },
        //facebook
        [EventTypes.FB_SERVICE_ACCOUNT_CONNECTED]: {
            title: 'ato.events.service.account.connected.title',
            desc: 'ato.events.meta.service.account.connected.desc'
        },
        [EventTypes.FB_SERVICE_ACCOUNT_UPDATED]: {
            title: 'ato.events.facebook.title',
            desc: 'ato.events.facebook.desc'
        },
        [EventTypes.FB_MONITORED_POSTS_NUM]: {
            title: 'ato.events.meta.monitored.posts.num.title',
            desc: 'ato.events.meta.monitored.posts.num.desc',
            one: {
                title: 'ato.events.meta.monitored.posts.num.title.singular'
            }
        },
        [EventTypes.FB_NEW_POST]: {
            title: 'ato.events.meta.new.post.title',
            desc: 'ato.events.meta.new.post.desc'
        },
        [EventTypes.FB_PROFILE_PHOTO_CHANGED]: {
            title: 'ato.events.fb.profile.photo.changed.title',
            desc: 'ato.events.fb.profile.photo.changed.desc',
        },
        [EventTypes.FB_COVER_PHOTO_CHANGED]: {
            title:  'ato.events.fb.cover.photo.changed.title',
            desc: 'ato.events.fb.cover.photo.changed.desc'
        },
        [EventTypes.FB_NAME_CHANGED]: {
            icon: {
                type: 'user-data',
                icon: 'name',
            },
            title: 'ato.events.meta.name.changed.title',
            desc: 'ato.events.meta.changed.desc'
        },
        [EventTypes.FB_USERNAME_CHANGED]: {
            icon: {
                type: 'user-data',
                icon: 'name',
            },
            title: 'ato.events.meta.username.changed.title',
            desc: 'ato.events.meta.changed.desc'
        },
        [EventTypes.FB_EMAIL_CHANGED]: {
            icon: {
                type: 'user-data',
                icon: 'email',
            },
            title: 'ato.events.fb.email.changed.title',
            desc: 'ato.events.meta.changed.desc'
        },
        [EventTypes.FB_PHONE_NUMBER_CHANGED]: {
            icon: {
                type: 'general',
                icon: 'phone',
            },
            title: 'ato.events.fb.phone.number.changed.title',
            desc: 'ato.events.meta.changed.desc'
        },
        [EventTypes.FB_NEW_BIOGRAPHY]: {
            icon: {
                type: 'web',
                icon: 'blogs'
            },
            title: 'ato.events.meta.new.bio.title',
            desc: 'ato.events.meta.new.bio.desc'
        },
        [EventTypes.FB_NEW_ALBUM]: {
            title: 'ato.events.fb.new.album.title',
            desc: 'ato.events.meta.new.post.desc',
            one: {
                title: 'ato.events.fb.new.album.title.singular'
            },
            empty: {
                title: 'ato.events.fb.new.album.title.empty'
            }
        },
        [EventTypes.FB_POST_DELETED]: {
            icon: {
                type: 'general',
                icon: 'photo'
            },
            title: 'ato.events.meta.post.deleted.title',
            desc: 'ato.events.meta.post.deleted.desc'
        },
        [EventTypes.FB_ALBUM_DELETED]: {
            icon: {
                type: 'general',
                icon: 'photo'
            },
            title: 'ato.events.fb.album.deleted.title',
            desc: 'ato.events.meta.post.deleted.desc'
        },
        [EventTypes.FB_POST_CHANGED]: {
            title: 'ato.events.meta.post.changed.title',
            desc: 'ato.events.meta.post.changed.desc'
        },
        [EventTypes.FB_ROLES_CHANGED]: {
            icon: {
                type: 'action',
                icon: 'add-account'
            },
            title: 'ato.events.fb.roles.changed.added.title',
            desc: 'ato.events.fb.roles.changed.added.desc',
            actionButton: {
                text: 'ato.review.button.label',
                link: 'https://business.facebook.com/settings/people'
            }
        },
        [EventTypes.FB_ROLES_CHANGED_REMOVED]: {
            icon: {
                type: 'action',
                icon: 'account-setting'
            },
            title: 'ato.events.fb.roles.changed.removed.title',
            desc: 'ato.events.fb.roles.changed.removed.desc',
            actionButton: {
                text: 'ato.review.button.label',
                link: 'https://business.facebook.com/settings/people'
            }
        },
        [EventTypes.FB_NEW_REEL]: {
            title: 'ato.events.fb.new.reel.title',
            desc: 'ato.events.meta.new.post.desc'
        },
        [EventTypes.FB_REEL_DELETED]: {
            icon: {
                type: 'web',
                icon: 'videos'
            },
            title: 'ato.events.fb.reel.deleted.title',
            desc: 'ato.events.meta.post.deleted.desc'
        },
        [EventTypes.FB_REVIEW_AUTHORIZED_MEMBERS]: {
            icon: {
                type: 'user-data',
                icon: 'list-check'
            },
            title: 'ato.events.review.authorized.members.title',
            desc: 'ato.events.review.authorized.members.desc',
            actionButton: {
                text: 'ato.review.team.button',
                link: 'https://business.facebook.com/settings/people'
            }
        },
        [EventTypes.FB_REVIEW_3RD_PARTY_APPS]: {
            icon: {
                type: 'menu',
                icon: 'apps'
            },
            title: 'ato.events.review.3rd.party.apps.title',
            desc: 'ato.events.review.3rd.party.apps.desc',
            actionButton: {
                text: 'ato.review.app.button',
                link: 'https://www.facebook.com/settings/?tab=applications'
            }
        },
        [EventTypes.FB_REVIEW_RECOVERY_OPTIONS]: {
            icon: {
                type: 'feature',
                icon: 'shield-speech'
            },
            title: 'ato.events.review.recovery.options.title',
            desc: 'ato.events.review.recovery.options.desc',
            actionButton: {
                text: 'ato.review.button',
                link: 'https://accountscenter.facebook.com/personal_info/contact_points'
            }
        },
        [EventTypes.FB_REGULARLY_CHANGE_PASSWORD]: {
            icon: {
                type: 'user-data',
                icon: 'password'
            },
            title: 'ato.events.regularly.change.password.title',
            desc: 'ato.events.regularly.change.password.desc',
            actionButton: {
                text: 'ato.change.button',
                link: 'https://accountscenter.facebook.com/password_and_security/password/change'
            }
        },
        [EventTypes.FB_REVIEW_YOUR_DEVICES]: {
            icon: {
                type: 'feature',
                icon: 'shield-speech'
            },
            title: 'ato.events.review.your.devices.title',
            desc: 'ato.events.review.your.devices.desc',
            actionButton: {
                text: 'ato.review.devices.button',
                link: 'https://accountscenter.facebook.com/password_and_security/login_activity'
            }
        }
    };

    public readonly threatEventCategoryInterface: ThreatEventCategoryInterface = {
        [ThreatEventCategory.FILE]: {
            bgColor: 'primary',
            texts: {
                [EventThreatType.MALWARE]: {
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.malware.file.title',
                        title: 'ato.threat.event.file.malware.title',
                        desc: 'ato.threat.event.file.malware.desc',
                        avertedOutcome: 'ato.threat.event.file.malware.averted.outcome'
                    }
                },
                [EventThreatType.FRAUD]: {
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.fraud.file.title',
                        title: 'ato.threat.event.file.fraud.title',
                        desc: 'ato.threat.event.file.fraud.desc',
                        avertedOutcome: 'ato.threat.event.file.fraud.averted.outcome'
                    }
                },
                [EventThreatType.TROJAN]: {
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.trojan.title',
                        title: 'ato.threat.event.file.trojan.title',
                        desc: 'ato.threat.event.file.trojan.desc',
                        avertedOutcome: 'ato.threat.event.file.trojan.averted.outcome'
                    }
                },
                [EventThreatType.ATTACK]: {
                    [EventThreatSubtype.BRUTEFORCE]: {
                        firstNodeTitle: 'ato.threat.without.rca.brute.force.title',
                        title: 'ato.threat.event.file.attack.brute.force.title',
                        desc: 'ato.threat.event.file.attack.brute.force.desc',
                        avertedOutcome: 'ato.threat.event.file.attack.brute.force.averted.outcome'
                    },
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.default',
                        title: 'ato.threat.event.file.general.title',
                        desc: 'ato.threat.event.file.general.desc',
                        avertedOutcome: 'ato.threat.event.file.general.averted.outcome'
                    }
                },
                [EventThreatType.RANSOMWARE]: {
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.ransomware.title',
                        title: 'ato.threat.event.file.ransomware.title',
                        desc: 'ato.threat.event.file.ransomware.desc',
                        avertedOutcome: 'ato.threat.event.file.ransomware.averted.outcome'
                    }
                },
                [EventThreatType.PUA]: {
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.pua.title',
                        title: 'ato.threat.event.file.pua.title',
                        desc: 'ato.threat.event.file.pua.desc',
                        avertedOutcome: 'ato.threat.event.file.pua.averted.outcome'
                    }
                },
                [EventThreatType.DEFAULT]: {
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.default',
                        title: 'ato.threat.event.file.general.title',
                        desc: 'ato.threat.event.file.general.desc',
                        avertedOutcome: 'ato.threat.event.file.general.averted.outcome'
                    }
                }
            }
        },
        [ThreatEventCategory.APP]: {
            bgColor: 'a2',
            texts: {
                [EventThreatType.MALWARE]: {
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.malware.app.title',
                        title: 'ato.threat.event.app.malware.title',
                        desc: 'ato.threat.event.app.malware.desc',
                        avertedOutcome: 'ato.threat.event.app.malware.averted.outcome'
                    }
                },
                [EventThreatType.EXPLOIT]: {
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.exploit.title',
                        title: 'ato.threat.event.app.exploit.title',
                        desc: 'ato.threat.event.app.exploit.desc',
                        avertedOutcome: 'ato.threat.event.app.exploit.averted.outcome'
                    }
                },
                [EventThreatType.RANSOMWARE]: {
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.ransomware.title',
                        title: 'ato.threat.event.app.ransomware.title',
                        desc: 'ato.threat.event.app.ransomware.desc',
                        avertedOutcome: 'ato.threat.event.app.ransomware.averted.outcome'
                    }
                },
                [EventThreatType.PUA]: {
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.pua.title',
                        title: 'ato.threat.event.app.pua.title',
                        desc: 'ato.threat.event.app.pua.desc',
                        avertedOutcome: 'ato.threat.event.app.pua.averted.outcome'
                    }
                },
                [EventThreatType.DEFAULT]: {
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.default',
                        title: 'ato.threat.event.file.general.title',
                        desc: 'ato.threat.event.app.general.desc',
                        avertedOutcome: 'ato.threat.event.app.general.averted.outcome'
                    }
                }
            }
        },
        [ThreatEventCategory.BEHAVIOUR]: {
            bgColor: 'a3',
            texts: {
                [EventThreatType.ATTACK]: {
                    [EventThreatSubtype.BRUTEFORCE]: {
                        firstNodeTitle: 'ato.threat.without.rca.brute.force.title',
                        title: 'ato.threat.event.file.attack.brute.force.title',
                        desc: 'ato.threat.event.file.attack.brute.force.desc',
                        avertedOutcome: 'ato.threat.event.file.attack.brute.force.averted.outcome'
                    },
                    [EventThreatSubtype.BRUTEFORCE_HTTP]: {
                        firstNodeTitle: 'ato.threat.without.rca.brute.force.title',
                        title: 'ato.threat.event.file.attack.brute.force.title',
                        desc: 'ato.threat.event.file.attack.brute.force.desc',
                        avertedOutcome: 'ato.threat.event.file.attack.brute.force.averted.outcome'
                    },
                    [EventThreatSubtype.LFI]: {
                        firstNodeTitle: 'ato.threat.without.rca.lfi.title',
                        title: 'ato.threat.event.behaviour.attack.lfi.title',
                        desc: 'ato.threat.event.behaviour.attack.lfi.desc',
                        avertedOutcome: 'ato.threat.event.behaviour.attack.lfi.averted.outcome'
                    },
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.default',
                        title: 'ato.threat.event.file.general.title',
                        desc: 'ato.threat.event.behaviour.general.desc',
                        avertedOutcome: 'ato.threat.event.behaviour.general.averted.outcome'
                    }
                },
                [EventThreatType.EXPLOIT]: {
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.exploit.title',
                        title: 'ato.threat.event.app.exploit.title',
                        desc: 'ato.threat.event.app.exploit.desc',
                        avertedOutcome: 'ato.threat.event.app.exploit.averted.outcome'
                    }
                },
                [EventThreatType.PRIVACY]: {
                    [EventThreatSubtype.PASSWORD]: {
                        firstNodeTitle: 'ato.threat.without.rca.privacy.password.title',
                        title: 'ato.threat.event.behaviour.privacy.password.title',
                        desc: 'ato.threat.event.behaviour.privacy.password.desc',
                        avertedOutcome: 'ato.threat.event.behaviour.privacy.password.averted.outcome'
                    },
                    [EventThreatSubtype.BANKING]: {
                        firstNodeTitle: 'ato.threat.without.rca.privacy.banking.title',
                        title: 'ato.threat.event.behaviour.privacy.banking.title',
                        desc: 'ato.threat.event.behaviour.privacy.banking.desc',
                        avertedOutcome: 'ato.threat.event.behaviour.privacy.banking.averted.outcome'
                    },
                    [EventThreatSubtype.DATA]: {
                        firstNodeTitle: 'ato.threat.without.rca.privacy.data.title',
                        title: 'ato.threat.event.behaviour.privacy.data.title',
                        desc: 'ato.threat.event.behaviour.privacy.data.desc',
                        avertedOutcome: 'ato.threat.event.behaviour.privacy.data.averted.outcome'
                    },
                    [EventThreatSubtype.LOGIN]: {
                        firstNodeTitle: 'ato.threat.without.rca.privacy.login.title',
                        title: 'ato.threat.event.behaviour.privacy.login.title',
                        desc: 'ato.threat.event.behaviour.privacy.login.desc',
                        avertedOutcome: 'ato.threat.event.behaviour.privacy.login.averted.outcome'
                    },
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.default',
                        title: 'ato.threat.event.file.general.title',
                        desc: 'ato.threat.event.behaviour.general.desc',
                        avertedOutcome: 'ato.threat.event.behaviour.general.averted.outcome'
                    }
                },
                [EventThreatType.BOT]: {
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.bot.title',
                        title: 'ato.threat.event.behaviour.bot.title',
                        desc: 'ato.threat.event.behaviour.bot.desc',
                        avertedOutcome: 'ato.threat.event.behaviour.bot.averted.outcome'
                    }
                },
                [EventThreatType.MALWARE]: {
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.malware.behaviour.title',
                        title: 'ato.threat.event.behaviour.malware.title',
                        desc: 'ato.threat.event.behaviour.malware.desc',
                        avertedOutcome: 'ato.threat.event.behaviour.malware.averted.outcome'
                    }
                },
                [EventThreatType.TROJAN]: {
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.trojan.title',
                        title: 'ato.threat.event.file.trojan.title',
                        desc: 'ato.threat.event.file.trojan.desc',
                        avertedOutcome: 'ato.threat.event.file.trojan.averted.outcome'
                    }
                },
                [EventThreatType.DEFAULT]: {
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.default',
                        title: 'ato.threat.event.file.general.title',
                        desc: 'ato.threat.event.behaviour.general.desc',
                        avertedOutcome: 'ato.threat.event.behaviour.general.averted.outcome'
                    }
                }
            }
        },
        [ThreatEventCategory.URL]: {
            bgColor: 'a4',
            texts: {
                [EventThreatType.CERTIFICATE]: {
                    [EventThreatSubtype.UNTRUSTED_ROOT]: {
                        firstNodeTitle: 'ato.threat.without.rca.certificate.title',
                        title: 'ato.threat.event.url.certificate.untrusted.root.title',
                        desc: 'ato.threat.event.url.certificate.untrusted.root.desc',
                        avertedOutcome: 'ato.threat.event.url.certificate.untrusted.root.averted.outcome'
                    },
                    [EventThreatSubtype.CERTIFICATE_WRONG_HOST]: {
                        firstNodeTitle: 'ato.threat.without.rca.certificate.title',
                        title: 'ato.threat.event.url.certificate.untrusted.root.title',
                        desc: 'ato.threat.event.url.certificate.wrong.host.desc',
                        avertedOutcome: 'ato.threat.event.url.certificate.wrong.host.averted.outcome'
                    },
                    [EventThreatSubtype.CERTIFICATE_EXPIRED]: {
                        firstNodeTitle: 'ato.threat.without.rca.certificate.title',
                        title: 'ato.threat.event.url.certificate.untrusted.root.title',
                        desc: 'ato.threat.event.url.certificate.certificate.expired.desc',
                        avertedOutcome: 'ato.threat.event.url.certificate.certificate.expired.averted.outcome'
                    },
                    [EventThreatSubtype.CERTIFICATE_REVOKED]: {
                        firstNodeTitle: 'ato.threat.without.rca.certificate.title',
                        title: 'ato.threat.event.url.certificate.untrusted.root.title',
                        desc: 'ato.threat.event.url.certificate.certificate.revoked.desc',
                        avertedOutcome: 'ato.threat.event.url.certificate.certificate.revoked.averted.outcome'
                    },
                    [EventThreatSubtype.CERTIFICATE_INVALID_SIGNATURE]: {
                        firstNodeTitle: 'ato.threat.without.rca.certificate.title',
                        title: 'ato.threat.event.url.certificate.untrusted.root.title',
                        desc: 'ato.threat.event.url.certificate.certificate.invalid.signature.desc',
                        avertedOutcome: 'ato.threat.event.url.certificate.certificate.invalid.signature.averted.outcome'
                    },
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.default',
                        title: 'ato.threat.event.file.general.title',
                        desc: 'ato.threat.event.behaviour.general.desc',
                        avertedOutcome: 'ato.threat.event.behaviour.general.averted.outcome'
                    }
                },
                [EventThreatType.MALWARE]: {
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.malware.title',
                        title: 'ato.threat.event.url.malware.title',
                        desc: 'ato.threat.event.url.malware.desc',
                        avertedOutcome: 'ato.threat.event.url.malware.averted.outcome'
                    }
                },
                [EventThreatType.FRAUD]: {
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.fraud.title',
                        title: 'ato.threat.event.url.fraud.title',
                        desc: 'ato.threat.event.url.fraud.desc',
                        avertedOutcome: 'ato.threat.event.url.fraud.averted.outcome'
                    }
                },
                [EventThreatType.PHISHING]: {
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.phishing.title',
                        title: 'ato.threat.event.url.phishing.title',
                        desc: 'ato.threat.event.url.phishing.desc',
                        avertedOutcome: 'ato.threat.event.url.phishing.averted.outcome'
                    }
                },
                [EventThreatType.MINER]: {
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.miner.title',
                        title: 'ato.threat.event.url.miner.title',
                        desc: 'ato.threat.event.url.miner.desc',
                        avertedOutcome: 'ato.threat.event.url.miner.averted.outcome'
                    }
                },
                [EventThreatType.PUA]: {
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.pua.link.title',
                        title: 'ato.threat.event.url.pua.title',
                        desc: 'ato.threat.event.url.pua.desc',
                        avertedOutcome: 'ato.threat.event.url.pua.averted.outcome'
                    }
                },
                [EventThreatType.UNTRUSTED]: {
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.untrusted.title',
                        title: 'ato.threat.event.url.untrusted.title',
                        desc: 'ato.threat.event.url.untrusted.desc',
                        avertedOutcome: 'ato.threat.event.url.untrusted.averted.outcome'
                    }
                },
                [EventThreatType.SPAM]: {
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.spam.title',
                        title: 'ato.threat.event.url.spam.title',
                        desc: 'ato.threat.event.url.spam.desc',
                        avertedOutcome: 'ato.threat.event.url.spam.averted.outcome'
                    }
                },
                [EventThreatType.MALICIOUS_HOST]: {
                    [EventThreatSubtype.MALWARE]: {
                        firstNodeTitle: 'ato.threat.without.rca.malware.title',
                        title: 'ato.threat.event.url.malware.title',
                        desc: 'ato.threat.event.url.malware.desc',
                        avertedOutcome: 'ato.threat.event.url.malware.averted.outcome'
                    },
                    [EventThreatSubtype.FRAUD]: {
                        firstNodeTitle: 'ato.threat.without.rca.fraud.title',
                        title: 'ato.threat.event.url.fraud.title',
                        desc: 'ato.threat.event.url.fraud.desc',
                        avertedOutcome: 'ato.threat.event.url.fraud.averted.outcome'
                    },
                    [EventThreatSubtype.PHISHING]: {
                        firstNodeTitle: 'ato.threat.without.rca.phishing.title',
                        title: 'ato.threat.event.url.phishing.title',
                        desc: 'ato.threat.event.url.phishing.desc',
                        avertedOutcome: 'ato.threat.event.url.phishing.averted.outcome'
                    },
                    [EventThreatSubtype.MINER]: {
                        firstNodeTitle: 'ato.threat.without.rca.miner.title',
                        title: 'ato.threat.event.url.miner.title',
                        desc: 'ato.threat.event.url.miner.desc',
                        avertedOutcome: 'ato.threat.event.url.miner.averted.outcome'
                    },
                    [EventThreatSubtype.PUA]: {
                        firstNodeTitle: 'ato.threat.without.rca.pua.link.title',
                        title: 'ato.threat.event.url.pua.title',
                        desc: 'ato.threat.event.url.pua.desc',
                        avertedOutcome: 'ato.threat.event.url.pua.averted.outcome'
                    },
                    [EventThreatSubtype.MALVERTISING]: {
                        firstNodeTitle: 'ato.threat.without.rca.malvertising.title',
                        title: 'ato.threat.event.url.malvertising.title',
                        desc: 'ato.threat.event.url.malvertising.desc',
                        avertedOutcome: 'ato.threat.event.url.malvertising.averted.outcome'
                    },
                    [EventThreatSubtype.SPAM]: {
                        firstNodeTitle: 'ato.threat.without.rca.spam.title',
                        title: 'ato.threat.event.url.spam.title',
                        desc: 'ato.threat.event.url.spam.desc',
                        avertedOutcome: 'ato.threat.event.url.spam.averted.outcome'
                    },
                    [EventThreatSubtype.UNTRUSTED]: {
                        firstNodeTitle: 'ato.threat.without.rca.untrusted.title',
                        title: 'ato.threat.event.url.untrusted.title',
                        desc: 'ato.threat.event.url.untrusted.desc',
                        avertedOutcome: 'ato.threat.event.url.untrusted.averted.outcome'
                    },
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.default',
                        title: 'ato.threat.event.file.general.title',
                        desc: 'ato.threat.event.behaviour.general.desc',
                        avertedOutcome: 'ato.threat.event.behaviour.general.averted.outcome'
                    }
                },
                [EventThreatType.DEFAULT]: {
                    [EventThreatSubtype.DEFAULT]: {
                        firstNodeTitle: 'ato.threat.without.rca.default',
                        title: 'ato.threat.event.file.general.title',
                        desc: 'ato.threat.event.file.general.desc',
                        avertedOutcome: 'ato.threat.event.file.general.averted.outcome'
                    }
                }
            }
        },
        [ThreatEventCategory.MOBILE]: {
            bgColor: 'danger'
        }
    };

    public readonly threatNodesInterface: ThreatEventsInterface = {
        [ThreatEventCategory.FILE]: {
            withRca: {
                firstNode: {
                    activeImage: this.images.threatEventWithRcaStartNode,
                    disabledImage: this.images.threatEventWithRcaStartNode,
                    baseColor: 'primary',
                    color: 'a4-5'
                },
                itemNode: {
                    activeImage: this.images.threatEventWithRcaNode,
                    disabledImage: this.images.threatEventWithRcaNode2,
                    baseColor: 'primary',
                    color: 'a4-5'
                },
                lastGoodNode: {
                    activeImage: this.images.threatEventSuccessNode,
                    disabledImage: this.images.threatEventSuccessNode,
                    baseColor: 'primary',
                    color: 'a4-5',
                    status: {
                        type: 'feedback-filled',
                        value: 'shield-check',
                        color: 'primary'
                    }
                },
                lastBadNode: {
                    activeImage: this.images.threatEventErrorNode,
                    disabledImage: this.images.threatEventErrorNode,
                    baseColor: 'danger',
                    color: 'danger',
                    status: {
                        type: 'feedback-filled',
                        value: 'shield-attention',
                        color: 'a7'
                    }
                }
            },
            default: {
                firstNode: {
                    icon: {
                        type: 'device',
                        value: 'computer'
                    },
                    baseColor: 'primary',
                    color: 'a4-5'
                },
                itemNode: {
                    icon: {
                        type: 'feature',
                        value: 'notes'
                    },
                    baseColor: 'primary',
                    color: 'a4-5',
                    desc: 'ato.threat.without.rca.item.node.file.desc'
                },
                lastGoodNode: {
                    activeImage: this.images.threatEventSuccessNode,
                    disabledImage: this.images.threatEventSuccessNode,
                    baseColor: 'primary',
                    color: 'a4-5',
                    status: {
                        type: 'feedback-filled',
                        value: 'shield-check',
                        color: 'primary'
                    }
                },
                lastBadNode: {
                    icon: {
                        type: 'feedback',
                        value: 'shield-x'
                    },
                    baseColor: 'danger',
                    color: 'danger',
                    status: {
                        type: 'feedback-filled',
                        value: 'shield-attention',
                        color: 'a7'
                    }
                }
            }
        },
        [ThreatEventCategory.APP]: {
            withRca: {
                firstNode: {
                    icon: {
                        type: 'device',
                        value: 'computer'
                    },
                    baseColor: 'primary',
                    color: 'a4-5'
                },
                itemNode: {
                    icon: {
                        type: 'menu',
                        value: 'apps'
                    },
                    baseColor: 'primary',
                    color: 'a4-5'
                },
                lastGoodNode: {
                    activeImage: this.images.threatEventSuccessNode,
                    disabledImage: this.images.threatEventSuccessNode,
                    baseColor: 'primary',
                    color: 'a4-5',
                    status: {
                        type: 'feedback-filled',
                        value: 'shield-check',
                        color: 'primary'
                    }
                },
                lastBadNode: {
                    activeImage: this.images.threatEventErrorNode,
                    disabledImage: this.images.threatEventErrorNode,
                    baseColor: 'danger',
                    color: 'danger',
                    status: {
                        type: 'feedback-filled',
                        value: 'shield-attention',
                        color: 'a7'
                    }
                }
            },
            default: {
                firstNode: {
                    icon: {
                        type: 'device',
                        value: 'computer'
                    },
                    baseColor: 'primary',
                    color: 'a4-5'
                },
                itemNode: {
                    icon: {
                        type: 'menu',
                        value: 'apps'
                    },
                    baseColor: 'primary',
                    color: 'a4-5',
                    desc: 'ato.threat.without.rca.item.node.app.desc'
                },
                lastGoodNode: {
                    activeImage: this.images.threatEventSuccessNode,
                    disabledImage: this.images.threatEventSuccessNode,
                    baseColor: 'primary',
                    color: 'a4-5',
                    status: {
                        type: 'feedback-filled',
                        value: 'shield-check',
                        color: 'primary'
                    }
                },
                lastBadNode: {
                    icon: {
                        type: 'feedback',
                        value: 'shield-x'
                    },
                    baseColor: 'danger',
                    color: 'danger',
                    status: {
                        type: 'feedback-filled',
                        value: 'shield-attention',
                        color: 'a7'
                    }
                }
            }
        },
        [ThreatEventCategory.BEHAVIOUR]: {
            default: {
                firstNode: {
                    icon: {
                        type: 'device',
                        value: 'computer'
                    },
                    baseColor: 'primary',
                    color: 'a4-5'
                },
                itemNode: {
                    icon: {
                        type: 'general',
                        value: 'curved-arrow-circle-x'
                    },
                    baseColor: 'primary',
                    color: 'a4-5',
                    desc: 'ato.threat.without.rca.item.node.behaviour.desc'
                },
                lastGoodNode: {
                    activeImage: this.images.threatEventSuccessNode,
                    disabledImage: this.images.threatEventSuccessNode,
                    baseColor: 'primary',
                    color: 'a4-5',
                    status: {
                        type: 'feedback-filled',
                        value: 'shield-check',
                        color: 'primary'
                    }
                },
                lastBadNode: {
                    icon: {
                        type: 'feedback',
                        value: 'shield-x'
                    },
                    baseColor: 'danger',
                    color: 'danger',
                    status: {
                        type: 'feedback-filled',
                        value: 'shield-attention',
                        color: 'a7'
                    }
                }
            }
        },
        [ThreatEventCategory.URL]: {
            default: {
                firstNode: {
                    icon: {
                        type: 'device',
                        value: 'computer'
                    },
                    baseColor: 'primary',
                    color: 'a4-5'
                },
                itemNode: {
                    icon: {
                        type: 'feature',
                        value: 'online'
                    },
                    baseColor: 'primary',
                    color: 'a4-5',
                    desc: 'ato.threat.without.rca.item.node.behaviour.desc'
                },
                lastGoodNode: {
                    activeImage: this.images.threatEventSuccessNode,
                    disabledImage: this.images.threatEventSuccessNode,
                    baseColor: 'primary',
                    color: 'a4-5',
                    status: {
                        type: 'feedback-filled',
                        value: 'shield-check',
                        color: 'primary'
                    }
                },
                lastBadNode: {
                    icon: {
                        type: 'feedback',
                        value: 'shield-x'
                    },
                    baseColor: 'danger',
                    color: 'danger',
                    status: {
                        type: 'feedback-filled',
                        value: 'shield-attention',
                        color: 'a7'
                    }
                }
            }
        }
    };

    public readonly explainableThreatsDetails: IExplainableThreatsDetails = {
        [ExplainableThreatsCodes.SPYING_ACTIVITIES]: {
            icon: { type: 'threat', icon: 'monitor' }
        },
        [ExplainableThreatsCodes.DEVICE_CONTROL]: {
            icon: { type: 'general', icon: 'phone-arrow-right' }
        },
        [ExplainableThreatsCodes.DEFENSIVE_EVASION]: {
            icon: { type: 'general', icon: 'curved-arrow-circle-x' }
        },
        [ExplainableThreatsCodes.HIDING_FROM_USER]: {
            icon: { type: 'threat', icon: 'hidden' }
        },
        [ExplainableThreatsCodes.PHISHING_ATTEMPT]: {
            icon: { type: 'threat', icon: 'banker' }
        },
        [ExplainableThreatsCodes.MONEY_EXTORTION]: {
            icon: { type: 'general', icon: 'hand-money' }
        },
        [ExplainableThreatsCodes.SPREADING]: {
            icon: { type: 'general', icon: 'arrows-circle' }
        },
        [ExplainableThreatsCodes.NETWORK_ACTIVITIES]: {
            icon: { type: 'general', icon: 'rectangle-graph' }
        },
        [ExplainableThreatsCodes.BITDEFENDER_VERDICT]: {
            icon: { type: 'products', icon: 'security-free' }
        },
        [ExplainableThreatsCodes.APPLICATION_DETAILS]: {
            icon: { type: 'general', icon: 'rectangle-info' }
        },
        [ExplainableThreatsCodes.FINANCIAL_RISK]: {
            icon: { type: 'general', icon: 'money-bag-alert' }
        },
        [ExplainableThreatsCodes.ADWARE_ACTIVITIES]: {
            icon: { type: 'threat', icon: 'adware' }
        }
    };

    public readonly iconsBasedOnTypes: ThreatTypeIcons = {
        [EventThreatType.MALWARE]: {
            [EventThreatSubtype.DEFAULT]: {
                type: 'threat',
                value: 'malware'
            }
        },
        [EventThreatType.TROJAN]: {
            [EventThreatSubtype.DEFAULT]: {
                type: 'web',
                value: 'kids'
            }
        },
        [EventThreatType.EXPLOIT]: {
            [EventThreatSubtype.DEFAULT]: {
                type: 'user-data',
                value: 'password-encoded'
            }
        },
        [EventThreatType.ATTACK]: {
            [EventThreatSubtype.BRUTEFORCE]: {
                type: 'action',
                value: 'manual-fix'
            },
            [EventThreatSubtype.BRUTEFORCE_HTTP]: {
                type: 'action',
                value: 'manual-fix'
            },
            [EventThreatSubtype.LFI]: {
                type: 'generic',
                value: 'bug'
            }
        },
        [EventThreatType.PRIVACY]: {
            [EventThreatSubtype.PASSWORD]: {
                type: 'user-data',
                value: 'password'
            },
            [EventThreatSubtype.BANKING]: {
                type: 'user-data',
                value: 'payment-methods'
            },
            [EventThreatSubtype.DATA]: {
                type: 'feature',
                value: 'profiling-targeting'
            },
            [EventThreatSubtype.LOGIN]: {
                type: 'user-data',
                value: 'password-decoded'
            }
        },
        [EventThreatType.RANSOMWARE]: {
            [EventThreatSubtype.DEFAULT]: {
                type: 'threat',
                value: 'ransomware'
            }
        },
        [EventThreatType.PUA]: {
            [EventThreatSubtype.DEFAULT]: {
                type: 'threat',
                value: 'pua'
            }
        },
        [EventThreatType.BOT]: {
            [EventThreatSubtype.DEFAULT]: {
                type: 'feedback',
                value: 'chat-bot'
            }
        },
        [EventThreatType.FRAUD]: {
            [EventThreatSubtype.DEFAULT]: {
                type: 'user-data',
                value: 'payment-methods'
            }
        },
        [EventThreatType.CERTIFICATE]: {
            [EventThreatSubtype.DEFAULT]: {
                type: 'feature',
                value: 'blocked-pages',
            }
        },
        [EventThreatType.PHISHING]: {
            [EventThreatSubtype.DEFAULT]: {
                type: 'threat',
                value: 'phishing'
            }
        },
        [EventThreatType.MINER]: {
            [EventThreatSubtype.DEFAULT]: {
                type: 'threat',
                value: 'coinminer'
            }
        },
        [EventThreatType.MALICIOUS_HOST]: {
            [EventThreatSubtype.MALWARE]: {
                type: 'threat',
                value: 'malware'
            },
            [EventThreatSubtype.FRAUD]: {
                type: 'user-data',
                value: 'payment-methods'
            },
            [EventThreatSubtype.PHISHING]: {
                type: 'threat',
                value: 'phishing'
            },
            [EventThreatSubtype.MINER]: {
                type: 'threat',
                value: 'coinminer'
            },
            [EventThreatSubtype.PUA]: {
                type: 'threat',
                value: 'pua'
            },
            [EventThreatSubtype.SPAM]: {
                type: 'threat',
                value: 'spam'
            },
            [EventThreatSubtype.UNTRUSTED]: {
                type: 'feedback',
                value: 'promote'
            }
        },
        [EventThreatType.UNTRUSTED]: {
            [EventThreatSubtype.DEFAULT]: {
                type: 'threat',
                value: 'hidden'
            }
        },
        [EventThreatType.SPAM]: {
            [EventThreatSubtype.DEFAULT]: {
                type: 'threat',
                value: 'spam'
            }
        },
        [EventThreatType.DEFAULT]: {
            [EventThreatSubtype.DEFAULT]: {
                type: 'feedback',
                value: 'shield-x'
            }
        }
    };

    public readonly videoStatusesInterface: VideoStatusesInterface = {
        [VideoStatuses.PUBLIC]: {
            statusIcon: {
                type: 'general',
                icon: 'earth'
            },
            statusEmoji: '🌎',
            label: 'ato.video.status.public'
        },
        [VideoStatuses.PRIVATE]: {
            statusIcon: {
                type: 'action',
                icon: 'lock'
            },
            statusEmoji: '🔒',
            label: 'ato.video.status.private'
        },
        [VideoStatuses.UNLISTED]: {
            statusIcon: {
                type: 'general',
                icon: 'url'
            },
            statusEmoji: '🔗',
            label: 'ato.video.status.unlisted'
        }
    };

    public readonly pinnedEventsOrder = {
        [EventTypes.ACCOUNT_TAKEOVER]: 1,
        [EventTypes.FB_ACCOUNT_TAKEOVER]: 1,
        [EventTypes.IG_ACCOUNT_TAKEOVER]: 1,
        [EventTypes.DISCONNECTED_API]: 2,
        [EventTypes.IG_DISCONNECTED_API]: 2,
        [EventTypes.FB_DISCONNECTED_API]: 2,
        [EventTypes.FINISH_TS_INSTALLATION]: 3,
        [EventTypes.ACTIVATE_WEBMAIL_PROTECTION]: 4,
        [EventTypes.TEAM_INVITE]: 5,
        [EventTypes.DEVICE_RISK_WEB_ATTACK_PREVENTION_OFF]: 6,
        [EventTypes.DEVICE_RISK_ADT_OFF]: 7,
        [EventTypes.DEVICE_RISK_SHIELD_OFF]: 8,
        [EventTypes.WEBMAIL_PROTECTION_DISCONNECTED]: 9,
        [EventTypes.DEVICE_DISCONNECTED]: 10,
        [EventTypes.INSTALL_TS_ON_ANOTHER_DEVICE]: 11,
        [EventTypes.REVIEW_AUTHORIZED_MEMBERS]: 12,
        [EventTypes.REVIEW_3RD_PARTY_APPS]: 13,
        [EventTypes.REGULARLY_CHANGE_PASSWORD]: 14,
        [EventTypes.REVIEW_YOUR_DEVICES]: 15,
        [EventTypes.REVIEW_RECOVERY_OPTIONS]: 16
    };

    public readonly ImageFields = {
        AVATAR_BINARY_DATA_URL: 'avatar_binary_data_url',
        AVATAR_BINARY_DATA: 'avatar_binary_data',
        STANDARD_QUALITY_BINARY_URL: 'standard_quality_binary_url',
        STANDARD_QUALITY_BINARY: 'standard_quality_binary',
        LAST_THREE_VIDEOS_THUMBNAILS_URL: 'last_three_videos_thumbnails_url',
        LAST_THREE_VIDEOS_THUMBNAILS: 'last_three_videos_thumbnails',
        BINARY_URL: 'binary_url',
        BINARY: 'binary',
        AVATAR_BINARY_URL: 'avatar_binary_url',
        AVATAR_BINARY: 'avatar_binary',
        BANNER_BINARY_URL: 'banner_binary_url',
        BANNER_BINARY: 'banner_binary',
        MAXRES_URL: 'maxres_url',
        URL: 'url',
        DISPLAY_URL: 'display_url',
        AVATAR_DISPLAY_URL: 'avatar_display_url',
        AVATAR_URL: 'avatar_url',
        BANNER_DISPLAY_URL: 'banner_display_url',
        AVATAR_BINARY_BD_URL: 'avatar_binary_bd_url',
        PROFILE_PICTURE_URL: 'profile_picture_url',
        PROFILE_PICTURE_URL_BD_URL: 'profile_picture_url_bd_url',
        THUMBNAIL_URL_BD_URL: 'thumbnail_url_bd_url',
        THUMBNAIL_URL: 'thumbnail_url'
    };

    public readonly providersCfg : Record<AtoProviders,AtoProviderConfig> = {
        [AtoProviders.YOUTUBE]: {
            id: AtoProviders.YOUTUBE,
            icon: this.images.youtubeLogo,
            title: 'YouTube',
            company: "Google",
            cta: 'ato.setup.connect.youtube.cta',
            desc: 'ato.setup.connect.youtube.desc'
        },
        [AtoProviders.INSTAGRAM]: {
            id: AtoProviders.INSTAGRAM,
            icon: this.images.instagramLogo,
            title: 'Instagram',
            company: "Meta",
            cta: 'ato.setup.connect.instagram.cta',
            desc: 'ato.setup.connect.instagram.desc'
        },
        [AtoProviders.FACEBOOK]: {
            id: AtoProviders.FACEBOOK,
            icon: this.images.facebookLogo,
            title: 'Facebook',
            company: "Meta",
            cta: 'ato.setup.connect.facebook.cta',
            desc: 'ato.setup.connect.facebook.desc'
        }
    };
}
